import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { PhoneInput } from "@/components/PhoneInput";
import { Sunrise, Save } from "lucide-react";
import { toast } from "sonner";

const FIELDS = [["start_time", "Arrival time", "9:00 AM"], ["contact_name", "Ask for", "Jane Doe, Office Manager"], ["dress_code", "Dress code", "Business casual"], ["parking", "Parking / arrival", "Visitor lot behind the building; check in at reception"]];

export function FirstDayPacketCard({ companyId, isAdmin }) {
  const [t, setT] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const params = isAdmin ? { company_id: companyId } : {};
  useEffect(() => { if (companyId) api.get("/first-day-packet", { params }).then((r) => setT(r.data)).catch(() => {}); }, [companyId]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k, v) => setT((p) => ({ ...p, [k]: v }));
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put("/first-day-packet", t, { params }); setT(data); toast.success("First-day packet saved"); setOpen(false); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  if (!t) return null;
  return (
    <Card className="p-4 space-y-3" data-testid="first-day-card">
      <div className="flex items-center justify-between gap-2">
        <div><div className="font-display font-semibold flex items-center gap-1.5 text-sm"><Sunrise className="w-4 h-4 text-amber-500" /> First-day packet</div><p className="text-xs text-muted-foreground">Emailed automatically the day before each start date — {t.start_time}{t.contact_name && ` · ask for ${t.contact_name}`}{t.address && ` · ${t.address}`}</p></div>
        <Button size="sm" variant="outline" data-testid="first-day-toggle" onClick={() => setOpen((o) => !o)}>{open ? "Hide" : "Edit packet"}</Button>
      </div>
      {open && (<>
        <div className="grid sm:grid-cols-2 gap-3">
          {FIELDS.map(([k, l, ph]) => <div key={k} className="space-y-1"><Label className="text-xs">{l}</Label><Input data-testid={`fd-${k}`} value={t[k] || ""} onChange={(e) => set(k, e.target.value)} placeholder={ph} /></div>)}
          <div className="space-y-1"><Label className="text-xs">Contact phone</Label><PhoneInput data-testid="fd-contact_phone" value={t.contact_phone || ""} onChange={(v) => set("contact_phone", v)} /></div>
          <div className="space-y-1"><Label className="text-xs">Address</Label><Input data-testid="fd-address" value={t.address || ""} onChange={(e) => set("address", e.target.value)} /></div>
        </div>
        <div className="space-y-1"><Label className="text-xs">What to bring</Label><Textarea data-testid="fd-bring" rows={2} value={t.bring || ""} onChange={(e) => set("bring", e.target.value)} /></div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="space-y-1"><Label className="text-xs">Day-one schedule</Label><Textarea data-testid="fd-schedule" rows={3} value={t.schedule || ""} onChange={(e) => set("schedule", e.target.value)} placeholder={"9:00 Welcome & paperwork\n10:00 IT setup\n12:00 Team lunch"} /></div>
          <div className="space-y-1"><Label className="text-xs">Notes</Label><Textarea data-testid="fd-notes" rows={3} value={t.notes || ""} onChange={(e) => set("notes", e.target.value)} /></div>
        </div>
        <div className="flex justify-end"><Button data-testid="fd-save" onClick={save} disabled={busy}><Save className="w-4 h-4 mr-1.5" /> Save packet</Button></div>
      </>)}
    </Card>
  );
}
